import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromInformation from '@core/store/reducers/information.reducer';
import * as fromLoginMessages from '@core/store/reducers/login-messages.reducer';
import * as fromUserLoginMessages from '@core/store/reducers/user-login-messages.reducer';
import * as fromUserInformation from '@core/store/reducers/user-information.reducer';
import * as fromForms from '@core/store/reducers/forms.reducer';
import * as fromFeed from '@core/store/reducers/feed.reducer';
import * as fromRoot from '../../../reducers';
import { InformationGroup, LoginMessage } from '@model';

// feature state
export interface InformationState {
  information: fromInformation.State;
  loginMessages: fromLoginMessages.State;
  userLoginMessages: fromUserLoginMessages.State;
  userInformations: fromUserInformation.State;
  forms: fromForms.State;
  feed: fromFeed.State;
}

export interface State extends fromRoot.State {
  information: InformationState;
}

// reducers
export const reducers: ActionReducerMap<InformationState> = {
  information: fromInformation.reducer,
  loginMessages: fromLoginMessages.reducer,
  userLoginMessages: fromUserLoginMessages.reducer,
  userInformations: fromUserInformation.reducer,
  forms: fromForms.reducer,
  feed: fromFeed.reducer
};

export const getState = createFeatureSelector<InformationState>('information');

// Information, Banner
export const getInformationState = createSelector(
  getState,
  (state: InformationState) => state.information
);
export const getInformation = createSelector(
  getInformationState,
  fromInformation.getInformation
);
export const getInformationLoaded = createSelector(
  getInformationState,
  fromInformation.getInformationLoaded
);

export const getBannerMessagesLoaded = createSelector(
  getInformationState,
  fromInformation.getBannerMessagesLoaded
);
export const getInformationLoading = createSelector(
  getInformationState,
  fromInformation.getInformationLoading
);
export const getBannerMessages = createSelector(
  getInformationState,
  fromInformation.getBannerMessages
);
export const getContextualBannerMessages = createSelector(
  getInformationState,
  fromInformation.getContextualBannerMessages
);

export const getStartPageBanner = createSelector(
  getInformationState,
  fromInformation.getStartPageBanner
);

export const getPreClosureBanner = createSelector(
  getInformationState,
  fromInformation.getPreClosureBanner
);

export const getAvtalInformation = createSelector(
  getInformationState,
  fromInformation.getAvtalInformation
);
export const getAvtalFAQ = createSelector(
  getInformationState,
  fromInformation.getAvtalFAQ
);
export const getAvtalsTillvalFAQFJV = createSelector(
  getInformationState,
  fromInformation.getAvtalsTillvalFAQFJV
);
export const getAvtalsTillvalFAQFJK = createSelector(
  getInformationState,
  fromInformation.getAvtalsTillvalFAQFJK
);
export const getMeteringPointInformations = createSelector(
  getInformationState,
  fromInformation.getMeteringPointInformations
);
export const getIntelligyInformations = createSelector(
  getInformationState,
  fromInformation.getIntelligyInformations
);

// LoginMessages
export const getLoginMessagesState = createSelector(
  getState,
  (state: InformationState) => state.loginMessages
);

export const getLoginMessagesLoaded = createSelector(
  getLoginMessagesState,
  fromLoginMessages.getLoginMessagesLoaded
);
export const getLoginMessages = createSelector(
  getLoginMessagesState,
  fromLoginMessages.getLoginMessages
);

// UserLoginMessages
export const getUserLoginMessagesState = createSelector(
  getState,
  (state: InformationState) => state.userLoginMessages
);

export const getUserLoginMessagesLoaded = createSelector(
  getUserLoginMessagesState,
  fromUserLoginMessages.getUserLoginMessagesLoaded
);
export const getUserLoginMessages = createSelector(
  getUserLoginMessagesState,
  fromUserLoginMessages.getUserLoginMessages
);

// UserInformation
export const getUserInformationState = createSelector(
  getState,
  (state: InformationState) => state.userInformations
);

export const getUserInformationsLoaded = createSelector(
  getUserInformationState,
  fromUserInformation._getUserInformationsLoaded
);
export const getUserInformationsLoading = createSelector(
  getUserInformationState,
  fromUserInformation._getUserInformationsLoading
);
export const getUserInformations = createSelector(
  getUserInformationState,
  fromUserInformation._getUserInformations
);

// Forms
export const getFormsState = createSelector(
  getState,
  (state: InformationState) => state.forms
);
export const getForms = createSelector(
  getFormsState,
  fromForms.getForms
);
export const getFormsLoaded = createSelector(
  getFormsState,
  fromForms.getFormsLoaded
);

// RSS Feed
export const getFeedState = createSelector(
  getState,
  (state: InformationState) => state.feed
);
export const getFeed = createSelector(
  getFeedState,
  fromFeed.getFeed
);
export const getFeedLoaded = createSelector(
  getFeedState,
  fromFeed.getFeedLoaded
);

// Combined Selectors
export const getCombinedLoginMessagesData = createSelector(
  getLoginMessagesState,
  getUserLoginMessagesState,
  (lmState, ulmState): LoginMessage[] => {
    if (
      !lmState.loginMessagesLoaded ||
      !ulmState.userLoginMessagesLoaded ||
      !ulmState.userLoginMessages ||
      !lmState.loginMessages
    ) {
      return;
    }

    return lmState.loginMessages.map(lm => {
      let hasViewed = ulmState.userLoginMessages.find(ulm => ulm.loginMessageId === lm.id);
      if (!hasViewed) {
        return { ...lm, isRead: false };
      }
      if (new Date(hasViewed.lastViewed) < new Date(lm.changed)) {
        return { ...lm, isRead: false };
      }
      return { ...lm, isRead: true };
    });
  }
);


export const getCombinedInformationGroupData = createSelector(
  getInformationState,
  getUserInformationState,
  (iState, uiState): InformationGroup => {
    if (!iState.loaded || !uiState.loaded || !iState.data || !uiState.userInformations) {
      return;
    }

    const informationGroup = iState.data.informationGroup;
    const informations = iState.data.informationGroup?.informations; // Item last in array marked as expanded, rest expanded set to false.
    
    if (informationGroup && informations.length > 0) {
      const combinedInformations = informationGroup.informations?.map(x => {
        let read =
          (uiState.userInformations.length !== 0) &&
          uiState.userInformations.find(
            ui => ui.informationId === x.id && ui.isRead && new Date(ui.readDate) > new Date(x.publishedDate)
          )
            ? true
            : false;
            
        return { ...x, isRead: read, expanded: x.expanded };
      });
      const newInformationGroup = {...informationGroup,
        informations: combinedInformations,
        unReadCount: combinedInformations.filter(x => !x.isRead).length};
      return {...newInformationGroup};
    }

    return {...informationGroup};
  }
);

export const getCombinedInformationGroupDataLoaded = createSelector(
  getInformationState,
  getUserInformationState,
  (iState, uiState) => {
    return iState.loaded && uiState.loaded;
  }
);


